import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { formatQuantity } from '@fewerbags/core';
import { STORAGE_LOCATIONS } from '@fewerbags/schema';
import type { PantryItemDto } from '@fewerbags/schema';
import { Alert } from '../../components/Alert';
import { AppShell } from '../../components/AppShell';
import { buttonClass } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { PlusIcon, SearchIcon } from '../../components/icons';
import { PantryIcon } from '../../components/NavIcons';
import { Section } from '../../components/Section';
import { Skeleton } from '../../components/Skeleton';
import { AddPantryItemForm } from '../../components/pantry/AddPantryItemForm';
import { ExpiryBadge } from '../../components/pantry/ExpiryBadge';
import { PantryItemDialog } from '../../components/pantry/PantryItemDialog';
import { api, queryKeys } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { LOCATION_LABEL, type StorageLocation } from '../../lib/pantryDisplay';
import { useDebouncedValue } from '../../lib/useDebouncedValue';

/** Colour key per storage location (pantry orange, fridge teal, freezer green). */
const LOCATION_DOT: Record<StorageLocation, string> = {
  pantry: 'bg-orange',
  fridge: 'bg-brand',
  freezer: 'bg-accent',
};

const EXPIRING_OPTIONS = [
  { value: '', label: 'Any time' },
  { value: '1', label: 'Within 1 day' },
  { value: '3', label: 'Within 3 days' },
  { value: '7', label: 'Within a week' },
  { value: '14', label: 'Within 2 weeks' },
];

export function PantryPage() {
  const [params, setParams] = useSearchParams();
  const [showAdd, setShowAdd] = useState(false);
  const [selectedItem, setSelectedItem] = useState<PantryItemDto | null>(null);

  const location = (params.get('location') as StorageLocation | null) ?? '';
  const expiringWithinDays = params.get('expiringWithinDays') ?? '';
  const qParam = params.get('q') ?? '';
  const [qInput, setQInput] = useState(qParam);
  const debouncedQ = useDebouncedValue(qInput, 250);

  function updateParam(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  }

  const query = {
    ...(location ? { location } : {}),
    ...(expiringWithinDays ? { expiringWithinDays: Number(expiringWithinDays) } : {}),
    ...(debouncedQ.trim() ? { q: debouncedQ.trim() } : {}),
  };

  const pantry = useQuery({
    queryKey: queryKeys.pantryList(query),
    queryFn: () => api.pantry.list(query),
  });

  return (
    <AppShell title="Pantry" width="wide">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
        <div className="flex flex-col gap-4 lg:sticky lg:top-6 lg:w-80 lg:shrink-0">
          <Section
            title="Add item"
            accent="green"
            icon={<PantryIcon className="size-6" />}
            description="Search the ingredient catalogue, or type a new name."
          >
            {showAdd ? (
              <AddPantryItemForm onAdded={() => undefined} />
            ) : (
              <button type="button" onClick={() => setShowAdd(true)} className={buttonClass()}>
                <PlusIcon className="size-4" /> Add a pantry item
              </button>
            )}
          </Section>

          <div className="flex flex-col gap-3 card p-4">
            <div className="flex flex-col gap-1">
              <label htmlFor="pantry-search" className="text-sm font-medium text-ink">
                Search
              </label>
              <div className="relative">
                <SearchIcon className="pointer-events-none absolute top-1/2 left-3 size-5 -translate-y-1/2 text-muted" />
                <input
                  id="pantry-search"
                  type="search"
                  value={qInput}
                  placeholder="Find an item…"
                  onChange={(e) => {
                    setQInput(e.target.value);
                    updateParam('q', e.target.value);
                  }}
                  className="min-h-11 w-full rounded-lg border border-line bg-surface pr-3 pl-10 text-base text-ink placeholder:text-muted"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by location">
              <button
                type="button"
                onClick={() => updateParam('location', '')}
                aria-pressed={location === ''}
                className={`min-h-9 rounded-full border px-3.5 text-sm font-medium transition-colors ${
                  location === ''
                    ? 'border-accent bg-accent text-ink shadow-sm'
                    : 'border-line text-ink hover:bg-accent-soft'
                }`}
              >
                All
              </button>
              {STORAGE_LOCATIONS.map((loc) => (
                <button
                  key={loc}
                  type="button"
                  onClick={() => updateParam('location', location === loc ? '' : loc)}
                  aria-pressed={location === loc}
                  className={`min-h-9 rounded-full border px-3.5 text-sm font-medium transition-colors ${
                    location === loc
                      ? 'border-accent bg-accent text-ink shadow-sm'
                      : 'border-line text-ink hover:bg-accent-soft'
                  }`}
                >
                  {LOCATION_LABEL[loc]}
                </button>
              ))}
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="pantry-expiring" className="text-sm font-medium text-ink">
                Expiring
              </label>
              <select
                id="pantry-expiring"
                value={expiringWithinDays}
                onChange={(e) => updateParam('expiringWithinDays', e.target.value)}
                className="min-h-11 rounded-lg border border-line bg-surface px-3 text-base text-ink"
              >
                {EXPIRING_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-5">
          {pantry.isPending ? <Skeleton lines={5} label="Loading the pantry" /> : null}
          {pantry.isError ? <Alert tone="error">{messageForError(pantry.error)}</Alert> : null}
          {pantry.isSuccess && pantry.data.items.length === 0 ? (
            <div className="card">
              <EmptyState
                accent="green"
                icon={<PantryIcon className="size-full" />}
                title="Nothing here"
              >
                No pantry items match these filters.
              </EmptyState>
            </div>
          ) : null}

          {pantry.data?.groups.map((group) => (
            <section key={group.location} aria-labelledby={`pantry-group-${group.location}`}>
              <h2
                id={`pantry-group-${group.location}`}
                className="mb-2.5 flex items-center gap-2 text-base font-semibold text-brand"
              >
                <span
                  aria-hidden
                  className={`size-2.5 rounded-full ${LOCATION_DOT[group.location]}`}
                />
                {LOCATION_LABEL[group.location]}{' '}
                <span className="rounded-full bg-brand-soft px-2 text-xs font-semibold text-brand">
                  {group.items.length}
                </span>
              </h2>
              <ul className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
                {group.items.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedItem(item)}
                      className="card card-link flex min-h-16 w-full items-center justify-between gap-3 p-3.5 text-left"
                    >
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-ink">
                          {item.ingredientName}
                        </span>
                        <span className="block text-sm text-muted">
                          {formatQuantity(item.qty, item.unit)}
                        </span>
                      </span>
                      <ExpiryBadge daysUntilExpiry={item.daysUntilExpiry} />
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>

      <PantryItemDialog
        item={selectedItem}
        open={selectedItem !== null}
        onClose={() => setSelectedItem(null)}
      />
    </AppShell>
  );
}
